// Trust → Business identity (KYB) — the register, and the only hand that can
// override it.
//
// Founder, 11 Sep 2026: "iot-admin v2 super-admins should be able to freely
// manage and CRUD them." Freely, but never silently — every write from this
// screen carries a REASON to the server, which refuses without one.
//
// ── WHAT THIS SCREEN EXISTS TO SAY, IN ORDER ────────────────────────────────
//  1. WHAT IS ACTUALLY VERIFIED — counted, never assumed. It read 0 of 25 for
//     as long as this screen existed: gstin_verified_at has been NULL since
//     V263 because the async verifier its own comment promised was never
//     built. It is built now (billing-service/src/services/kybVerifier.js) and
//     this screen is where a person triggers it, one entity at a time, with
//     the customer's consent — see the Evidence block in the drawer. The count
//     still comes from the data, so the header goes green only as registries
//     actually answer.
//  2. WHICH ONES ARE ALREADY WRONG. Tier-1 costs nothing and it finds real
//     defects in live data: LEO1 declares itself an individual while the PAN
//     inside its own GSTIN says company; CabE and Rupyz carry a name whose
//     initial disagrees with that PAN. Those rows are flagged here, not in a
//     report nobody opens.
//  3. WHO SHARES A CREDENTIAL. Seventeen entities carry GaliGali's own GSTIN,
//     by founder ruling — a business may hold many portfolios and they do NOT
//     share a verification. Shown as a fact, never merged.
//
// ── THE WALL LIVES HERE TOO ─────────────────────────────────────────────────
// Three days' notice, "with ability to extend or reduce even to immediate"
// (founder, 12 Sep). It is a stored instant per tenant, not a constant, so the
// schedule is a control on this screen and every change to it is recorded with
// a reason in kyb.gate_policy_events.
//
// NO DELETE. An entity is closed by unbinding, never removed — a payout dispute
// six months from now needs the record that existed at the time. The server has
// no DELETE verb for an entity and this screen offers no button for one.

import React, { useState } from 'react';
import {
  PageHead, Loading, ErrorState, Pill, Button, Empty, DataTable, Tiles,
} from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import c from './Trust.module.css';

const CONSTITUTIONS = ['individual', 'sole_proprietorship', 'partnership', 'llp',
  'private_limited', 'public_limited', 'trust_society', 'other'];
const STATUSES = ['draft', 'submitted', 'verified', 'refer', 'rejected', 'lapsed'];

const STATUS_TONE = {
  verified: 'grow', submitted: 'iris', draft: 'pend',
  refer: 'warn', rejected: 'bad', lapsed: 'warn',
};
const WALL_TONE = {
  armed: 'bad', notice_period: 'warn', exempt: 'iris',
  not_scheduled: 'pend', plane_absent: 'pend',
};
const WALL_LABEL = {
  armed: 'Wall live', notice_period: 'Notice running', exempt: 'Exempt',
  not_scheduled: 'Not scheduled', plane_absent: 'Not deployed',
};

const human = (s) => String(s || '').replace(/_/g, ' ');
const fmtWhen = (d) => (d
  ? new Date(d).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  })
  : '—');

// ── tier 1, in the browser ──────────────────────────────────────────────────
// A deliberate copy of the two cheapest checks in lambda/billing-service/src/
// lib/kyb.js, so a reviewer sees the defect on the row without a round trip.
// The SERVER remains the authority — this only decorates. A copy that drifts is
// a lie, so it is limited to the two rules that cannot change: the PAN sits at
// GSTIN[2..12], and its 4th character is the constitution.
const PAN_TYPE = {
  C: ['private_limited', 'public_limited'],
  P: ['individual', 'sole_proprietorship'],
  F: ['partnership', 'llp'],
  T: ['trust_society'],
};
function tier1(e) {
  const flags = [];
  const pan = e.pan || (e.gstin && e.gstin.length === 15 ? e.gstin.slice(2, 12) : null);
  if (!pan) return flags;
  const allows = PAN_TYPE[pan[3]];
  if (allows && e.constitution && !allows.includes(e.constitution)) {
    flags.push({ tone: 'bad', label: 'constitution', title: `PAN says ${pan[3] === 'C' ? 'company' : pan[3] === 'P' ? 'individual' : pan[3] === 'F' ? 'firm or LLP' : 'trust'}, declared ${human(e.constitution)}` });
  }
  if (e.legal_name) {
    const initial = e.legal_name.trim()[0]?.toUpperCase();
    if (initial && initial !== pan[4]) {
      flags.push({ tone: 'warn', label: 'name', title: `PAN belongs to a name starting '${pan[4]}', legal name starts '${initial}'` });
    }
  }
  return flags;
}

// ── the override drawer ─────────────────────────────────────────────────────
function EntityDrawer({ entityId, onClose, onSaved }) {
  const detail = useApi(() => api.get(`/v2/kyb/entities/${entityId}`).then((r) => r.data?.data), [entityId]);
  const [patch, setPatch] = useState({});
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [consent, setConsent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [vErr, setVErr] = useState(null);
  const [vNote, setVNote] = useState(null);

  if (detail.loading) return <div className={c.drawerEmpty}><Loading what="entity" /></div>;
  if (detail.error) return <div className={c.drawerEmpty}><ErrorState error={detail.error} onRetry={detail.reload} /></div>;
  if (!detail.data || !detail.data.entity) {
    return (
      <div className={c.drawerEmpty}>
        <Empty title="Could not load that entity">It may have been unbound since this list was loaded.</Empty>
        <Button onClick={onClose}>Close</Button>
      </div>
    );
  }

  const e = detail.data.entity;
  const next = { ...e, ...patch };
  const dirty = Object.keys(patch).length > 0;
  const set = (k) => (ev) => setPatch((p) => ({ ...p, [k]: ev.target.value === '' ? null : ev.target.value }));

  async function save() {
    setBusy(true); setErr(null);
    try {
      await api.patch(`/v2/kyb/entities/${entityId}`, { ...patch, reason });
      setPatch({}); setReason('');
      await detail.reload();
      onSaved?.();
    } catch (e2) {
      setErr(e2?.response?.data?.error || e2.message);
    } finally { setBusy(false); }
  }

  // The outcome is stated in the words the reviewer needs, not the service's
  // enum: what was asked, what came back, and whether a person still has to
  // look. Reuse is said out loud — an operator who clicks twice and sees the
  // same timestamp should know why, not assume the button is broken.
  async function runVerify() {
    setVerifying(true); setVErr(null); setVNote(null);
    try {
      const r = await api.post(`/v2/kyb/entities/${entityId}/verify`, { consent: true });
      const out = r.data?.data || {};
      if (out.skipped) {
        setVNote(out.detail || 'Nothing was checked.');
      } else {
        const said = (out.results || []).map((x) => {
          const what = x.kind === 'gstin' ? 'GSTIN' : 'PAN';
          if (x.reused) {
            return x.reason === 'still_valid'
              ? `${what}: already confirmed, still within its 180 days`
              : `${what}: checked moments ago, reusing that answer`;
          }
          if (x.outcome === 'match') return `${what}: confirmed as ${x.returned_name}`;
          if (x.outcome === 'not_found') return `${what}: the registry has no such number`;
          if (x.outcome === 'mismatch') return `${what}: registered to ${x.returned_name || 'someone else'}`;
          return `${what}: no answer from the registry`;
        });
        setVNote([...said, out.note].filter(Boolean).join(' · '));
      }
      await detail.reload();
      onSaved?.();
    } catch (e2) {
      setVErr(e2?.response?.data?.error || e2.message);
    } finally { setVerifying(false); }
  }

  const field = (label, key, { mono } = {}) => (
    <label className={c.field}>
      <span className={c.label}>{label}</span>
      <input className={mono ? `${c.input} ${c.mono}` : c.input}
        value={next[key] ?? ''} onChange={set(key)} />
    </label>
  );

  return (
    <>
      <div className={c.drawerHead}>
        <div>
          <h3 className={c.drawerTitle}>{e.display_name}</h3>
          <div className={c.drawerSub}>
            {human(e.constitution)} · <Pill tone={STATUS_TONE[e.status]}>{e.status}</Pill>
          </div>
        </div>
        <Button onClick={onClose}>Close</Button>
      </div>
      <div className={c.drawerBody}>

      {/* Tier-1 findings first: a reviewer should see the defect before the
          form, not after saving something on top of it. */}
      {tier1(next).length > 0 && (
        <div className={c.findings}>
          {tier1(next).map((f, i) => (
            <div key={i} className={c.finding}>
              <Pill tone={f.tone}>{f.label}</Pill> <span>{f.title}</span>
            </div>
          ))}
        </div>
      )}

      <div className={c.formGrid}>
        {field('Display name', 'display_name')}
        {field('Legal name (as per GSTN)', 'legal_name')}
        <label className={c.field}>
          <span className={c.label}>Constitution</span>
          <select className={c.input} value={next.constitution || ''} onChange={set('constitution')}>
            {CONSTITUTIONS.map((v) => <option key={v} value={v}>{human(v)}</option>)}
          </select>
        </label>
        {field('GSTIN', 'gstin', { mono: true })}
        {field('PAN', 'pan', { mono: true })}
        <label className={c.field}>
          <span className={c.label}>Status</span>
          <select className={c.input} value={next.status || ''} onChange={set('status')}>
            {STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
      </div>

      {/* Setting 'verified' by hand writes a manual evidence row server-side,
          so the badge carries provenance. Said here so the reviewer knows the
          act is recorded, not just the value. */}
      {patch.status === 'verified' && e.status !== 'verified' && (
        <div className={c.noteBar}>
          Marking this verified records a manual verification against your name,
          with the reason below, in this entity’s evidence.
        </div>
      )}

      <label className={c.field}>
        <span className={c.label}>Reason (required — it goes in the audit trail)</span>
        <input className={c.input} value={reason} onChange={(ev) => setReason(ev.target.value)}
          placeholder="e.g. corrected constitution after reading the GST certificate" />
      </label>

      {err && <div className={c.badBar}>{err}</div>}

      <div className={c.actions}>
        <Button primary disabled={!dirty || reason.trim().length < 4 || busy} onClick={save}>
          {busy ? 'Saving…' : dirty ? `Save ${Object.keys(patch).length} change${Object.keys(patch).length === 1 ? '' : 's'}` : 'No changes'}
        </Button>
        {dirty && <Button onClick={() => setPatch({})}>Discard</Button>}
      </div>

      <div>
        <h4 className={c.subHead}>Bindings</h4>
        <DataTable
          columns={[
            { key: 'capacity', label: 'Capacity', render: (r) => human(r.capacity) },
            { key: 'tenant_name', label: 'Workspace' },
            { key: 'subject_type', label: 'Bound to', render: (r) => human(r.subject_type) },
            { key: 'valid_from', label: 'From', render: (r) => fmtWhen(r.valid_from) },
            { key: 'valid_to', label: 'Until', render: (r) => (r.valid_to ? fmtWhen(r.valid_to) : <Pill tone="grow">open</Pill>) },
          ]}
          rows={detail.data.bindings} getKey={(r) => r.id} />
      </div>

      <div>
        <h4 className={c.subHead}>Evidence</h4>

        {/* ── THE BUTTON THAT WAS MISSING ────────────────────────────────────
            Founder, 13 Sep: "Easebuzz has switched on for our key salt but on
            BM for eg still shows unverified for thyrocare both pan and gst."
            The adapter, the credentials, the evidence table and the badge all
            existed; nothing connected them, so the answer to "how does
            verification happen" was: it doesn't. This is where a person asks.

            Tier 1 runs free in the browser and the service repeats it before
            spending anything, so a self-contradicting entity is refused here
            with its reason rather than sent to an authority that cannot
            resolve it. And the consent tick is not decoration — the
            Verification Suite requires the merchant to hold the customer's
            consent, and the service rejects the call without it. */}
        {tier1(next).length > 0 ? (
          <div className={c.noteBar}>
            Fix the {tier1(next).length === 1 ? 'finding' : 'findings'} above first.
            An authority can confirm a credential; it cannot settle details that
            disagree with each other.
          </div>
        ) : !e.gstin && !e.pan ? (
          <div className={c.noteBar}>
            Add a GSTIN or a PAN — there is nothing here to ask about yet.
          </div>
        ) : (
          <>
            <label className={c.radio}>
              <input type="checkbox" checked={consent}
                onChange={(ev) => setConsent(ev.target.checked)} />
              <span>
                {e.display_name} has consented to us checking
                {e.gstin ? ' their GSTIN' : ''}{e.gstin && e.pan ? ' and' : ''}{e.pan ? ' their PAN' : ''}
                {' '}against the registry.
              </span>
            </label>
            {vErr && <div className={c.badBar}>{vErr}</div>}
            {vNote && <div className={c.noteBar}>{vNote}</div>}
            <div className={c.actions}>
              <Button primary disabled={!consent || verifying} onClick={runVerify}>
                {verifying ? 'Asking…' : 'Check with the registry'}
              </Button>
              <span className={c.hint}>
                Records what comes back as evidence below, whatever it says.
              </span>
            </div>
          </>
        )}

        {detail.data.evidence.length === 0
          ? <Empty title="Never verified">No authority has been asked about this entity.</Empty>
          : (
            <DataTable
              columns={[
                { key: 'credential_kind', label: 'Credential' },
                { key: 'provider', label: 'By' },
                { key: 'outcome', label: 'Outcome', render: (r) => <Pill tone={r.outcome === 'match' ? 'grow' : r.outcome === 'error' ? 'pend' : 'bad'}>{r.outcome}</Pill> },
                { key: 'returned_name', label: 'Name on record' },
                { key: 'checked_at', label: 'Checked', render: (r) => fmtWhen(r.checked_at) },
              ]}
              rows={detail.data.evidence.map((x, i) => ({ ...x, _k: `ev${i}` }))} getKey={(r) => r._k} />
          )}
      </div>

      <div>
        <h4 className={c.subHead}>Audit trail</h4>
        <DataTable
          columns={[
            { key: 'changed_at', label: 'When', render: (r) => fmtWhen(r.changed_at) },
            { key: 'field', label: 'Field' },
            { key: 'old_value', label: 'From', render: (r) => r.old_value ?? '—' },
            { key: 'new_value', label: 'To', render: (r) => r.new_value ?? '—' },
            { key: 'actor_type', label: 'By', render: (r) => <Pill tone={r.actor_type === 'super_admin' ? 'warn' : 'pend'}>{human(r.actor_type)}</Pill> },
            { key: 'reason', label: 'Why', render: (r) => r.reason ?? '—' },
          ]}
          rows={detail.data.events.map((x, i) => ({ ...x, _k: `ev${i}` }))} getKey={(r) => r._k} />
      </div>
      </div>
    </>
  );
}

// ── the wall schedule ───────────────────────────────────────────────────────
function WallSchedule() {
  const policy = useApi(() => api.get('/v2/kyb/gate-policy').then((r) => r.data?.data), []);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState(null);

  if (policy.loading) return <Loading what="wall schedule" />;
  if (policy.error) return <ErrorState error={policy.error} onRetry={policy.reload} />;
  if (!policy.data) return <Empty title="Schedule unavailable">The KYB plane is not deployed on this environment yet.</Empty>;

  async function setSchedule(tenantId, body, label) {
    const reason = window.prompt(`Reason for ${label}? (goes in the audit trail)`);
    if (!reason || reason.trim().length < 4) return;
    setBusyId(tenantId); setErr(null);
    try {
      await api.put(`/v2/kyb/gate-policy/${tenantId}`, { ...body, reason: reason.trim() });
      await policy.reload();
    } catch (e) { setErr(e?.response?.data?.error || e.message); } finally { setBusyId(null); }
  }

  return (
    <div className={c.findings}>
      <p className={c.sectionNote}>
        The Media Owner Console withholds itself from a workspace that has no payee identity —
        a legal name and a PAN, the two things a TDS deduction needs. Earnings keep accruing
        either way; the wall withholds the console, not the money. Default notice is{' '}
        {policy.data.default_notice_days} days.
      </p>
      {err && <div className={c.badBar}>{err}</div>}
      <DataTable
        columns={[
          { key: 'tenant_name', label: 'Workspace' },
          {
            key: 'wall_state',
            label: 'State',
            render: (r) => <Pill tone={WALL_TONE[r.wall_state]}>{WALL_LABEL[r.wall_state] || r.wall_state}</Pill>,
          },
          { key: 'has_media_identity', label: 'Payee identity', render: (r) => (r.has_media_identity ? <Pill tone="grow">declared</Pill> : <Pill tone="warn">none</Pill>) },
          { key: 'arms_at', label: 'Arms at', render: (r) => fmtWhen(r.arms_at) },
          { key: 'reason', label: 'Why', render: (r) => r.reason ?? '—' },
          {
            key: 'act',
            label: '',
            render: (r) => (
              <span className={c.flagRow}>
                <Button disabled={busyId === r.tenant_id}
                  onClick={() => setSchedule(r.tenant_id, { notice_days: policy.data.default_notice_days }, `${policy.data.default_notice_days} days' notice`)}>
                  {policy.data.default_notice_days}d notice
                </Button>
                <Button disabled={busyId === r.tenant_id}
                  onClick={() => setSchedule(r.tenant_id, { immediate: true }, 'arming the wall IMMEDIATELY')}>
                  Immediate
                </Button>
                <Button disabled={busyId === r.tenant_id || !r.arms_at}
                  onClick={() => setSchedule(r.tenant_id, { arms_at: null }, 'unscheduling the wall')}>
                  Unschedule
                </Button>
              </span>
            ),
          },
        ]}
        rows={policy.data.tenants || []} getKey={(r) => r.tenant_id} />
    </div>
  );
}


// ── invoice compliance ──────────────────────────────────────────────────────
// A REPORT, and the screen says so. The server refuses to block issuance and
// explains why in its own route comment: under Rule 47 / s.13(2) the liability
// and the time limit run against US, so withholding a document is a second
// breach rather than a cure. What a human can act on is here instead — which
// documents are already wrong, and which accounts will produce the next one.
function InvoiceCompliance() {
  const comp = useApi(() => api.get('/v2/kyb/invoice-compliance').then((r) => r.data?.data), []);
  if (comp.loading) return <Loading what="invoice compliance" />;
  if (comp.error) return <ErrorState error={comp.error} onRetry={comp.reload} />;
  if (!comp.data) return <Empty title="Compliance report unavailable">The KYB plane is not deployed on this environment yet.</Empty>;
  const d = comp.data;
  return (
    <div className={c.findings}>
      <p className={c.sectionNote}>{d.rule}</p>
      <p className={c.sectionNote}>
        Not enforced — issuance is deliberately never blocked. Holding a tax document parks
        our own liability, not the customer’s.
      </p>

      <div>
        <h4 className={c.subHead}>
          Will produce a deficient invoice next time ({(d.would_issue_deficient || []).length})
        </h4>
        {(d.would_issue_deficient || []).length === 0
          ? <Empty title="None">Every profile with a GSTIN carries a full address.</Empty>
          : (
            <DataTable
              columns={[
                { key: 'tenant_name', label: 'Workspace' },
                { key: 'legal_name', label: 'Legal name' },
                { key: 'gstin', label: 'GSTIN' },
                { key: 'missing', label: 'Missing', render: (r) => [
                  r.missing_address_line && 'address',
                  r.missing_pincode && 'pincode',
                  r.missing_state && 'state',
                ].filter(Boolean).join(', ') },
              ]}
              rows={(d.would_issue_deficient || []).map((x, i) => ({ ...x, _k: `p${i}` }))}
              getKey={(r) => r._k} />
          )}
      </div>

      <div>
        <h4 className={c.subHead}>Already issued and deficient ({(d.already_issued || []).length})</h4>
        {(d.already_issued || []).length === 0
          ? <Empty title="None">No issued invoice is missing a required field.</Empty>
          : (
            <DataTable
              columns={[
                { key: 'invoice_number', label: 'Invoice' },
                { key: 'tenant_name', label: 'Workspace' },
                { key: 'buyer_legal_name', label: 'Named buyer' },
                { key: 'total', label: 'Amount', align: 'num' },
                { key: 'why', label: 'Defect', render: (r) => (
                  <span className={c.flagRow}>
                    {r.missing_address && <Pill tone="bad">no address</Pill>}
                    {!r.buyer_legal_name && <Pill tone="bad">no name</Pill>}
                    {r.buyer_is_us && <Pill tone="warn">buyer is us</Pill>}
                  </span>
                ) },
                { key: 'created_at', label: 'Issued', render: (r) => fmtWhen(r.created_at) },
              ]}
              rows={(d.already_issued || []).map((x, i) => ({ ...x, _k: `i${i}` }))}
              getKey={(r) => r._k} />
          )}
      </div>
    </div>
  );
}

// ── the register ────────────────────────────────────────────────────────────
export default function Kyb() {
  const [capacity, setCapacity] = useState('');
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState(null);
  const reg = useApi(
    () => api.get(`/v2/kyb/entities?${new URLSearchParams({ ...(capacity && { capacity }), ...(status && { status }) })}`)
      .then((r) => r.data?.data),
    [capacity, status],
  );

  if (reg.loading) return <Loading what="the KYB register" />;
  if (reg.error) return <ErrorState error={reg.error} onRetry={reg.reload} />;
  // The server answered, but with no payload — the KYB plane probe returns a
  // 503 envelope with an explanation and no `data`. Say so rather than throwing
  // on a property of undefined, which renders a blank page.
  if (!reg.data) {
    return (
      <>
        <PageHead title="Business identity" sub="The register is not available yet." />
        <Empty title="Not deployed yet">
          The KYB plane has not been migrated on this environment (V357). Nothing is missing —
          there is simply nothing to show until it lands.
        </Empty>
      </>
    );
  }

  const rows = reg.data.entities || [];
  const verified = rows.filter((r) => r.status === 'verified').length;
  const withEvidence = rows.filter((r) => r.last_outcome && r.last_outcome !== 'error').length;
  const flagged = rows.filter((r) => tier1(r).some((f) => f.tone === 'bad')).length;
  const warned = rows.filter((r) => tier1(r).some((f) => f.tone === 'warn')).length;

  return (
    <>
      <PageHead
        title="Business identity"
        sub="Every legal entity in the estate, what we actually know about it, and the wall that will require it. Nothing here is verified until an authority says so."
      />

      {/* The counts lead with what is NOT known, because a compliance screen
          that opens on reassurance is the failure mode this plane exists to
          end. These move only when a registry answers — 'Verified by an
          authority' counts evidence, never a status somebody typed. */}
      <Tiles items={[
        { label: 'Entities', value: rows.length },
        { label: 'Verified by an authority', value: verified, tone: verified === 0 ? 'bad' : undefined },
        { label: 'Ever checked', value: withEvidence, tone: withEvidence === 0 ? 'bad' : undefined },
        { label: 'Failing a free check', value: flagged, tone: flagged ? 'bad' : undefined },
        { label: 'Name mismatch', value: warned, tone: warned ? 'warn' : undefined },
      ]} />

      <div className={c.controls}>
        <select className={c.select} value={capacity} onChange={(e) => setCapacity(e.target.value)}>
          <option value="">Both capacities</option>
          <option value="advertiser">Advertiser (they pay us)</option>
          <option value="media_owner">Media owner (we pay them)</option>
        </select>
        <select className={c.select} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any status</option>
          {STATUSES.map((s2) => <option key={s2} value={s2}>{s2}</option>)}
        </select>
      </div>

      <DataTable
        filterPlaceholder="Filter by workspace, name, GSTIN or PAN…"
        filterOn={['tenant_name', 'display_name', 'legal_name', 'gstin', 'pan']}
        initialSort={{ key: 'tenant_name', dir: 'asc' }}
        columns={[
          { key: 'tenant_name', label: 'Workspace' },
          { key: 'capacity', label: 'Capacity', render: (r) => human(r.capacity) },
          { key: 'display_name', label: 'Name' },
          { key: 'constitution', label: 'Constitution', render: (r) => human(r.constitution) },
          { key: 'gstin', label: 'GSTIN', render: (r) => r.gstin || '—' },
          { key: 'status', label: 'Status', render: (r) => <Pill tone={STATUS_TONE[r.status]}>{r.status}</Pill> },
          {
            key: 'last_outcome',
            label: 'Authority',
            render: (r) => (r.last_outcome
              ? <Pill tone={r.last_outcome === 'match' ? 'grow' : r.last_outcome === 'error' ? 'pend' : 'bad'}>{r.last_outcome}</Pill>
              : <span className={c.never}>never asked</span>),
          },
          {
            key: 'flags',
            label: 'Free checks',
            render: (r) => {
              const f = tier1(r);
              if (!f.length) return <span className={c.clean}>clean</span>;
              return <span className={c.flagRow}>{f.map((x, i) => <span key={i} title={x.title}><Pill tone={x.tone}>{x.label}</Pill></span>)}</span>;
            },
          },
          { key: 'act', label: '', render: (r) => <Button onClick={() => setOpenId(r.id)}>Review</Button> },
        ]}
        rows={rows} getKey={(r) => `${r.id}-${r.binding_id}`} />

      {/* A credential on more than one entity is a FACT, shown rather than
          resolved. Seventeen of these are GaliGali's own portfolios and that is
          deliberate — a business may hold many, and they do not share a
          verification (founder ruling, 11 Sep). It is shown because the other
          reason a GSTIN repeats is someone typing one that is not theirs. */}
      {(reg.data.shared_credentials || []).length > 0 && (
        <div className={c.section}>
          <h3 className={c.sectionHead}>Shared credentials</h3>
          <p className={c.sectionNote}>
            One GSTIN, several entities. Usually one business with several portfolios — each
            verifies for itself, deliberately. Occasionally it is a number that belongs to
            somebody else.
          </p>
          <DataTable
            columns={[
              { key: 'gstin', label: 'GSTIN' },
              { key: 'entities', label: 'Entities' },
              { key: 'names', label: 'Named', render: (r) => (r.names || []).join(', ') },
            ]}
            rows={reg.data.shared_credentials || []} getKey={(r) => r.gstin} />
        </div>
      )}

      <div className={c.section}>
        <h3 className={c.sectionHead}>The Media Owner Console wall</h3>
        <WallSchedule />
      </div>

      <div className={c.section}>
        <h3 className={c.sectionHead}>Invoice compliance</h3>
        <InvoiceCompliance />
      </div>

      {openId && (
        <div className={c.scrim} onClick={() => setOpenId(null)}>
          <div className={c.drawer} onClick={(e) => e.stopPropagation()}>
            <EntityDrawer entityId={openId} onClose={() => setOpenId(null)} onSaved={reg.reload} />
          </div>
        </div>
      )}
    </>
  );
}
